import React, { useState, useRef, FC } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SideMenu from "components/molecules/SideMenu";
import paths from "routes/paths";

import { AssessmentType } from "types";

import {
  Answer,
  AnswersContent,
  Question,
  QuestionContent,
  QuestionNumber,
  StyledButton,
  AnswersWrapper,
  DangerousInner,
} from "pages/Student/Tests/styles";

import MobileHeader from "components/atoms/MobileHeader";

import { getTestName } from "helpers/getTestName";

import InterestAnswerBar from "components/molecules/InterestAnswerBar";
import {
  Container,
  SideMenuWrapper,
  CardWrapper,
  SubTitle,
  Title,
  CorrectAnswer,
  YourTurn,
  CorrectAnswerHeader,
  StyledCard,
  ButtonWrapper,
  TryItOut,
  InterestQuestionContent,
  InterestAnswersWrapper,
  WrongAnswerIcon,
  CorrectAnswerIcon,
} from "./styles";

import { QuestionsListType } from "./types";

const Training: FC<{ isFreeTest?: boolean }> = ({ isFreeTest }) => {
  const { t } = useTranslation();
  const { type, id } = useParams<{ type: AssessmentType; id: string }>();
  const history = useHistory();

  const sideMenuValues = isFreeTest
    ? [
        { name: t("student.allAssessments"), path: paths.freeTestDashboard },
        {
          name: t("student.instructions"),
          path: `/individual/test-instruction/${type}/${id}`,
        },
      ]
    : [
        { name: t("student.allAssessments"), path: paths.studentDashboard },
        {
          name: t("student.instructions"),
          path: `/test-instruction/${type}/${id}`,
        },
      ];

  const [answers, setAnswers] = useState<{ [key: string]: number }>({});

  const finishTraining = () => {
    if (isFreeTest) {
      history.push(`/individual/training-finished/${type}/${id}`);
    } else {
      history.push(`/training-finished/${type}/${id}`);
    }
  };
  const scrollRef = useRef<HTMLDivElement>(null);

  const selectAnswer = (question: string, idx: number) => {
    if (answers[question] === undefined || question === "test") {
      setAnswers((oldAnswers) => ({ ...oldAnswers, [question]: idx }));
    }
  };

  const questionsList: QuestionsListType = {
    numerical: [
      {
        number: t("test.examples.numerical.1.number"),
        question: t("test.examples.numerical.1.question"),
        answers: [
          t("test.examples.numerical.1.answer1"),
          t("test.examples.numerical.1.answer2"),
          t("test.examples.numerical.1.answer3"),
          t("test.examples.numerical.1.answer4"),
          t("test.examples.numerical.1.answer5"),
        ],
        correct: 0,
        hint: `${t("test.examples.numerical.1.hint1")}, <strong>${t(
          "test.examples.numerical.1.answer1"
        )}</strong> ${t("test.examples.numerical.1.hint2")}.`,
      },
      {
        number: t("test.examples.numerical.2.number"),
        question: t("test.examples.numerical.2.question"),
        answers: [
          t("test.examples.numerical.2.answer1"),
          t("test.examples.numerical.2.answer2"),
          t("test.examples.numerical.2.answer3"),
          t("test.examples.numerical.2.answer4"),
          t("test.examples.numerical.2.answer5"),
        ],
        correct: 4,
        hint: `${t(
          "test.examples.numerical.2.hint1"
        )} <strong>€50.00</strong> ${t("test.examples.numerical.2.hint2")}.`,
      },
      {
        number: t("test.examples.numerical.3.number"),
        question: t("test.examples.numerical.3.question"),
        answers: [
          t("test.examples.numerical.3.answer1"),
          t("test.examples.numerical.3.answer2"),
          t("test.examples.numerical.3.answer3"),
          t("test.examples.numerical.3.answer4"),
          t("test.examples.numerical.3.answer5"),
        ],
        correct: 1,
        hint: t("test.examples.numerical.3.hint1"),
      },
      {
        number: t("test.examples.numerical.4.number"),
        question: `${t(
          "test.examples.numerical.4.question"
        )}?<br /> 1, 3, <strong>?</strong>, 7, 9, 11, 13`,
        answers: ["4", "6", "8", "5", "15"],
        correct: 3,
        hint: t("test.examples.numerical.4.hint1"),
      },
    ],
    verbal1: [
      {
        number: t("test.examples.verbal1.1.number"),
        question: `<strong>${t("test.examples.verbal1.1.question")}</strong>`,
        answers: [
          t("test.examples.verbal1.1.answer1"),
          t("test.examples.verbal1.1.answer2"),
          t("test.examples.verbal1.1.answer3"),
          t("test.examples.verbal1.1.answer4"),
        ],
        correct: 3,
        hint: `${t("test.examples.verbal1.1.hint1")}, <strong>${t(
          "test.examples.verbal1.1.answer4"
        )}</strong> ${t("test.examples.verbal1.1.hint2")} <strong>${t(
          "test.examples.verbal1.1.question"
        )}</strong> `,
      },
      {
        number: t("test.examples.verbal1.2.number"),
        question: `<strong>${t("test.examples.verbal1.2.question")}</strong>`,
        answers: [
          t("test.examples.verbal1.2.answer1"),
          t("test.examples.verbal1.2.answer2"),
          t("test.examples.verbal1.2.answer3"),
          t("test.examples.verbal1.2.answer4"),
        ],
        correct: 2,
        hint: `${t("test.examples.verbal1.2.hint1")}, <strong>${t(
          "test.examples.verbal1.2.answer3"
        )}</strong> ${t("test.examples.verbal1.2.hint2")} <strong>${t(
          "test.examples.verbal1.2.question"
        )}</strong>`,
      },
      {
        number: t("test.examples.verbal1.3.number"),
        question: `<strong>${t("test.examples.verbal1.3.question")}</strong>`,
        answers: [
          t("test.examples.verbal1.3.answer1"),
          t("test.examples.verbal1.3.answer2"),
          t("test.examples.verbal1.3.answer3"),
          t("test.examples.verbal1.3.answer4"),
        ],
        correct: 3,
        hint: `${t("test.examples.verbal1.3.hint1")}, <strong>${t(
          "test.examples.verbal1.3.answer4"
        )}</strong> ${t("test.examples.verbal1.3.hint2")} <strong>${t(
          "test.examples.verbal1.3.question"
        )}</strong> `,
      },
      {
        number: t("test.examples.verbal1.4.number"),
        question: `<strong>${t("test.examples.verbal1.4.question")}</strong>`,
        answers: [
          t("test.examples.verbal1.4.answer1"),
          t("test.examples.verbal1.4.answer2"),
          t("test.examples.verbal1.4.answer3"),
          t("test.examples.verbal1.4.answer4"),
        ],
        correct: 1,
        hint: `${t("test.examples.verbal1.3.hint1")}, <strong>${t(
          "test.examples.verbal1.4.answer2"
        )}</strong> ${t("test.examples.verbal1.4.hint2")} <strong>${t(
          "test.examples.verbal1.4.question"
        )}</strong>`,
      },
    ],
    verbal2: [
      {
        number: t("test.examples.verbal2.1.number"),
        question: t("test.examples.verbal2.1.question"),
        answers: [
          t("test.examples.verbal2.1.answer1"),
          t("test.examples.verbal2.1.answer2"),
          t("test.examples.verbal2.1.answer3"),
          t("test.examples.verbal2.1.answer4"),
          t("test.examples.verbal2.1.answer5"),
          t("test.examples.verbal2.1.answer6"),
        ],
        correct: 5,
        hint: t("test.examples.verbal2.1.hint1"),
      },
      {
        number: t("test.examples.verbal2.2.number"),
        question: t("test.examples.verbal2.2.question"),
        answers: [
          t("test.examples.verbal2.2.answer1"),
          t("test.examples.verbal2.2.answer2"),
          t("test.examples.verbal2.2.answer3"),
          t("test.examples.verbal2.2.answer4"),
          t("test.examples.verbal2.2.answer5"),
          t("test.examples.verbal2.2.answer6"),
        ],
        correct: 2,
        hint: t("test.examples.verbal2.2.hint1"),
      },
      {
        number: t("test.examples.verbal2.3.number"),
        question: t("test.examples.verbal2.3.question"),
        answers: [
          t("test.examples.verbal2.3.answer1"),
          t("test.examples.verbal2.3.answer2"),
          t("test.examples.verbal2.3.answer3"),
          t("test.examples.verbal2.3.answer4"),
          t("test.examples.verbal2.3.answer5"),
          t("test.examples.verbal2.3.answer6"),
        ],
        correct: 0,
        hint: t("test.examples.verbal2.3.hint1"),
      },
      {
        number: t("test.examples.verbal2.4.number"),
        question: t("test.examples.verbal2.4.question"),
        answers: [
          t("test.examples.verbal2.4.answer1"),
          t("test.examples.verbal2.4.answer2"),
          t("test.examples.verbal2.4.answer3"),
          t("test.examples.verbal2.4.answer4"),
          t("test.examples.verbal2.4.answer5"),
          t("test.examples.verbal2.4.answer6"),
        ],
        correct: 4,
        hint: t("test.examples.verbal2.4.hint1"),
      },
    ],
    verbal3: [
      {
        number: t("test.examples.verbal3.1.number"),
        question: t("test.examples.verbal3.1.question"),
        answers: [
          t("test.examples.verbal3.1.answer1"),
          t("test.examples.verbal3.1.answer2"),
          t("test.examples.verbal3.1.answer3"),
          t("test.examples.verbal3.1.answer4"),
          t("test.examples.verbal3.1.answer5"),
        ],
        correct: 2,
        hint: t("test.examples.verbal3.1.hint1"),
      },
      {
        number: t("test.examples.verbal3.2.number"),
        question: t("test.examples.verbal3.2.question"),
        answers: [
          t("test.examples.verbal3.2.answer1"),
          t("test.examples.verbal3.2.answer2"),
          t("test.examples.verbal3.2.answer3"),
          t("test.examples.verbal3.2.answer4"),
          t("test.examples.verbal3.2.answer5"),
        ],
        correct: 4,
        hint: t("test.examples.verbal3.2.hint1"),
      },
      {
        number: t("test.examples.verbal3.3.number"),
        question: t("test.examples.verbal3.3.question"),
        answers: [
          t("test.examples.verbal3.3.answer1"),
          t("test.examples.verbal3.3.answer2"),
          t("test.examples.verbal3.3.answer3"),
          t("test.examples.verbal3.3.answer4"),
          t("test.examples.verbal3.3.answer5"),
        ],
        correct: 3,
        hint: t("test.examples.verbal3.3.hint1"),
      },
    ],
    abstract: [
      {
        number: t("test.examples.abstract.1.number"),
        question: t("test.examples.abstract.1.question"),
        answers: [
          "<img src='/images/ex1-1.svg' />",
          "<img src='/images/ex1-2.svg' />",
          "<img src='/images/ex1-3.svg' />",
          "<img src='/images/ex1-4.svg' />",
          "<img src='/images/ex1-5.svg' />",
          "<img src='/images/ex1-6.svg' />",
        ],
        correct: 1,
        hint: t("test.examples.abstract.1.hint1"),
      },
      {
        number: t("test.examples.abstract.2.number"),
        question: t("test.examples.abstract.2.question"),
        answers: [
          "<img src='/images/ex2-1.svg' />",
          "<img src='/images/ex2-2.svg' />",
          "<img src='/images/ex2-3.svg' />",
          "<img src='/images/ex2-4.svg' />",
          "<img src='/images/ex2-5.svg' />",
          "<img src='/images/ex2-6.svg' />",
        ],
        correct: 2,
        hint: t("test.examples.abstract.2.hint1"),
      },
      {
        number: t("test.examples.abstract.3.number"),
        question: t("test.examples.abstract.3.question"),
        answers: [
          "<img src='/images/ex3-1.svg' />",
          "<img src='/images/ex3-2.svg' />",
          "<img src='/images/ex3-3.svg' />",
          "<img src='/images/ex3-4.svg' />",
          "<img src='/images/ex3-5.svg' />",
          "<img src='/images/ex3-6.svg' />",
        ],
        correct: 3,
        hint: t("test.examples.abstract.3.hint1"),
      },
      {
        number: t("test.examples.abstract.4.number"),
        question: t("test.examples.abstract.4.question"),
        answers: [
          "<img src='/images/ex4-1.svg' />",
          "<img src='/images/ex4-2.svg' />",
          "<img src='/images/ex4-3.svg' />",
          "<img src='/images/ex4-4.svg' />",
          "<img src='/images/ex4-5.svg' />",
          "<img src='/images/ex4-6.svg' />",
        ],
        correct: 3,
        hint: t("test.examples.abstract.4.hint1"),
      },
    ],
    interest: [
      {
        number: "",
        question: "",
        answers: [],
        correct: 0,
        hint: "",
      },
    ],
  };

  const questions = questionsList[type];

  const aptitudeAnswers = [
    t("test.examples.aptitudeAnswers.0"),
    t("test.examples.aptitudeAnswers.1"),
    t("test.examples.aptitudeAnswers.2"),
    t("test.examples.aptitudeAnswers.3"),
    t("test.examples.aptitudeAnswers.4"),
    t("test.examples.aptitudeAnswers.5"),
    t("test.examples.aptitudeAnswers.6"),
  ];

  const getHtmlQuestion = (question: string) => {
    return {
      __html: question,
    };
  };

  return (
    <div>
      <MobileHeader
        title={sideMenuValues[sideMenuValues.length - 1].name}
        onClick={() =>
          history.push(sideMenuValues[sideMenuValues.length - 1].path)
        }
      />
      <Container>
        <SideMenuWrapper>
          <SideMenu values={sideMenuValues} />
        </SideMenuWrapper>
        <CardWrapper>
          <StyledCard>
            <SubTitle>{getTestName(type)}</SubTitle>
            <Title>{t("student.training")}</Title>
          </StyledCard>
          {type === "interest" ? (
            <>
              <Question>
                <TryItOut>{t("student.tryItOut")}</TryItOut>
                <InterestQuestionContent>
                  {t("test.examples.interest.1.question")}
                </InterestQuestionContent>
                <InterestAnswersWrapper>
                  <InterestAnswerBar
                    question="test"
                    clickHandler={selectAnswer}
                    answers={answers}
                    parentQuestion=""
                  />
                </InterestAnswersWrapper>
                {typeof answers.test !== "undefined" && (
                  <InterestAnswersWrapper>
                    <AnswersWrapper>
                      <CorrectAnswer
                        dangerouslySetInnerHTML={getHtmlQuestion(
                          aptitudeAnswers[answers.test]
                        )}
                      />
                    </AnswersWrapper>
                  </InterestAnswersWrapper>
                )}
              </Question>
              {typeof answers.test !== "undefined" && (
                <ButtonWrapper ref={scrollRef}>
                  <StyledButton variant="primary" onClick={finishTraining}>
                    {t("student.finishTraining")}
                  </StyledButton>
                </ButtonWrapper>
              )}
            </>
          ) : (
            <>
              <Question key={questions[0].number}>
                <QuestionNumber>{questions[0].number}</QuestionNumber>
                <QuestionContent
                  dangerouslySetInnerHTML={getHtmlQuestion(
                    questions[0].question
                  )}
                />
                <AnswersWrapper>
                  <AnswersContent>
                    {questions[0].answers.map((answer, idx) => (
                      <Answer
                        key={idx}
                        selected={idx === questions[0].correct}
                        notInteractive
                        dangerouslySetInnerHTML={getHtmlQuestion(answer)}
                      />
                    ))}
                  </AnswersContent>
                  <AnswersWrapper>
                    <CorrectAnswer
                      dangerouslySetInnerHTML={getHtmlQuestion(
                        questions[0].hint
                      )}
                    />
                  </AnswersWrapper>
                </AnswersWrapper>
              </Question>
              <YourTurn>
                <strong>{t("student.yourTurn")}</strong>
                <div
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={getHtmlQuestion(
                    t("student.readQuestions")
                  )}
                />
              </YourTurn>
              <Question key={questions[1].number}>
                <QuestionNumber>{questions[1].number}</QuestionNumber>
                <QuestionContent
                  dangerouslySetInnerHTML={getHtmlQuestion(
                    questions[1].question
                  )}
                />
                <AnswersWrapper>
                  <AnswersContent>
                    {questions[1].answers.map((answer, idx) => (
                      <Answer
                        key={idx}
                        selected={
                          answers[questions[1].number] !== undefined &&
                          questions[1].correct === idx
                        }
                        wrong={
                          answers[questions[1].number] !== undefined &&
                          questions[1].correct !== idx &&
                          answers[questions[1].number] === idx
                        }
                        onClick={() => selectAnswer(questions[1].number, idx)}
                        notInteractive={
                          answers[questions[1].number] !== undefined
                        }
                        dangerouslySetInnerHTML={getHtmlQuestion(answer)}
                      />
                    ))}
                  </AnswersContent>
                  {answers[questions[1].number] !== undefined && (
                    <AnswersWrapper>
                      <CorrectAnswer>
                        {answers[questions[1].number] ===
                        questions[1].correct ? (
                          <CorrectAnswerIcon />
                        ) : (
                          <WrongAnswerIcon />
                        )}
                        <CorrectAnswerHeader>
                          {answers[questions[1].number] === questions[1].correct
                            ? t("student.thatsCorrect")
                            : t("student.thatsWrong")}
                        </CorrectAnswerHeader>
                        <DangerousInner
                          dangerouslySetInnerHTML={getHtmlQuestion(
                            questions[1].hint
                          )}
                        />
                      </CorrectAnswer>
                    </AnswersWrapper>
                  )}
                </AnswersWrapper>
              </Question>
              {answers[questions[1].number] !== undefined && (
                <>
                  <YourTurn ref={scrollRef}>
                    <strong>{t("student.howAbout")}</strong>
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={getHtmlQuestion(
                        t("student.readQuestions")
                      )}
                    />
                  </YourTurn>
                  <Question key={questions[2].number}>
                    <QuestionNumber>{questions[2].number}</QuestionNumber>
                    <QuestionContent
                      dangerouslySetInnerHTML={getHtmlQuestion(
                        questions[2].question
                      )}
                    />
                    <AnswersWrapper>
                      <AnswersContent>
                        {questions[2].answers.map((answer, idx) => (
                          <Answer
                            key={idx}
                            selected={
                              answers[questions[2].number] !== undefined &&
                              questions[2].correct === idx
                            }
                            wrong={
                              answers[questions[2].number] !== undefined &&
                              questions[2].correct !== idx &&
                              answers[questions[2].number] === idx
                            }
                            onClick={() =>
                              selectAnswer(questions[2].number, idx)
                            }
                            notInteractive={
                              answers[questions[2].number] !== undefined
                            }
                            dangerouslySetInnerHTML={getHtmlQuestion(answer)}
                          />
                        ))}
                      </AnswersContent>
                      {answers[questions[2].number] !== undefined && (
                        <AnswersWrapper>
                          <CorrectAnswer
                            icon={
                              answers[questions[2].number] ===
                              questions[2].correct
                                ? "correct"
                                : "wrong"
                            }
                          >
                            <CorrectAnswerHeader>
                              {answers[questions[2].number] ===
                              questions[2].correct
                                ? t("student.thatsCorrect")
                                : t("student.thatsWrong")}
                            </CorrectAnswerHeader>
                            <DangerousInner
                              dangerouslySetInnerHTML={getHtmlQuestion(
                                questions[2].hint
                              )}
                            />
                          </CorrectAnswer>
                        </AnswersWrapper>
                      )}
                    </AnswersWrapper>
                  </Question>
                </>
              )}
              {answers[questions[2].number] !== undefined && questions[3] && (
                <>
                  <YourTurn ref={scrollRef}>
                    <strong>{t("student.lastOne")}</strong>
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={getHtmlQuestion(
                        t("student.readQuestions")
                      )}
                    />
                  </YourTurn>
                  <Question key={questions[3].number}>
                    <QuestionNumber>{questions[3].number}</QuestionNumber>
                    <QuestionContent
                      dangerouslySetInnerHTML={getHtmlQuestion(
                        questions[3].question
                      )}
                    />
                    <AnswersWrapper>
                      <AnswersContent>
                        {questions[3].answers.map((answer, idx) => (
                          <Answer
                            key={idx}
                            selected={
                              answers[questions[3].number] !== undefined &&
                              questions[3].correct === idx
                            }
                            wrong={
                              answers[questions[3].number] !== undefined &&
                              questions[3].correct !== idx &&
                              answers[questions[3].number] === idx
                            }
                            onClick={() =>
                              selectAnswer(questions[3].number, idx)
                            }
                            notInteractive={
                              answers[questions[3].number] !== undefined
                            }
                            dangerouslySetInnerHTML={getHtmlQuestion(answer)}
                          />
                        ))}
                      </AnswersContent>
                      {answers[questions[3].number] !== undefined && (
                        <AnswersWrapper>
                          <CorrectAnswer
                            icon={
                              answers[questions[3].number] ===
                              questions[3].correct
                                ? "correct"
                                : "wrong"
                            }
                          >
                            <CorrectAnswerHeader>
                              {answers[questions[3].number] ===
                              questions[3].correct
                                ? t("student.thatsCorrect")
                                : t("student.thatsWrong")}
                            </CorrectAnswerHeader>

                            <DangerousInner
                              dangerouslySetInnerHTML={getHtmlQuestion(
                                questions[3].hint
                              )}
                            />
                          </CorrectAnswer>
                        </AnswersWrapper>
                      )}
                    </AnswersWrapper>
                  </Question>
                </>
              )}
              {answers[questions[questions.length - 1].number] !==
                undefined && (
                <ButtonWrapper ref={scrollRef}>
                  <StyledButton variant="primary" onClick={finishTraining}>
                    {t("student.finishTraining")}
                  </StyledButton>
                </ButtonWrapper>
              )}
            </>
          )}
        </CardWrapper>
      </Container>
    </div>
  );
};

export default Training;
